// API client
import { apiClient } from '../client';

// Types
import type { ApiEvent, FulfilmentType, PublishBlocker } from './index';
import type { EventFormState } from '../../components/OrderDetails/CreateEvent/CreateEventForm/CreateEventForm';
import { combineDateAndTime } from '../../utils/dateTime';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CreateEventMetadata {
  form?: string;
  product_line?: string;
  tags?: string[];
  serving_time?: string;
  description?: string;
  special_instructions?: string;
}

export interface CreateEventLogisticsInfo {
  lines?: number;
  ways?: number;
}

export interface CreateEventPayload {
  scheduled_at: string;
  pax_count: number;
  metadata: CreateEventMetadata;
  logistics_info?: CreateEventLogisticsInfo;
}

export interface CreateEventResponse {
  event: ApiEvent;
}

export interface CreateEventsParams {
  orderId: number;
  events: CreateEventPayload[];
}

export interface CreateEventsResult {
  created: ApiEvent[];
  errors: Error[];
}

// ─── API functions ──────────────────────────────────────────────────────────

export async function createEvent(orderId: number, payload: CreateEventPayload): Promise<ApiEvent> {
  const response = await apiClient.post<CreateEventResponse>(
    `/api/orders/${orderId}/events`,
    { event: payload },
  );
  return response.data.event;
}

export async function createEvents(params: CreateEventsParams): Promise<CreateEventsResult> {
  const results = await Promise.allSettled(
    params.events.map((event) => createEvent(params.orderId, event)),
  );

  const created: ApiEvent[] = [];
  const errors: Error[] = [];

  for (const result of results) {
    if (result.status === 'fulfilled') {
      created.push(result.value);
    } else {
      errors.push(result.reason instanceof Error ? result.reason : new Error(String(result.reason)));
    }
  }

  return { created, errors };
}

// ─── Update Event Types ─────────────────────────────────────────────────────

/**
 * `null` is an explicit clear. `Events::UpdateEvent` merges this hash over the
 * stored metadata so an omitted key keeps its persisted value; only a key that
 * is actually present with a null/empty value clears.
 */
export interface UpdateEventMetadata {
  form?: string | null;
  product_line?: string | null;
  tags?: string[];
  occasions?: string[];
  occasions_note?: string | null;
  serving_time?: string | null;
  description?: string | null;
  special_instructions?: string | null;
  organisation_notes?: string | null;
  event_requirements?: { toggles: string[]; stairs: boolean };
}

export interface UpdateEventLogisticsInfo {
  lines?: number;
  ways?: number;
  /** `null` is an explicit clear (GRAIN-2406); a published event rejects a blank address with a 422. */
  venue_address?: string | null;
  venue_unit?: string | null;
  postal_code?: string | null;
  location_notes?: string;
}

export interface UpdateEventHostInfo {
  primary_host_name?: string;
  primary_host_number?: string;
  secondary_host_name?: string;
  secondary_host_number?: string;
  customer_notes?: string;
}

export interface UpdateEventLineItemAttributes {
  id?: number;
  item_type?: 'Product' | 'ResaleItem';
  item_id?: number;
  quantity?: number;
  unit_price?: number;
  position?: number;
  dish_instructions_food?: string;
  dish_instructions_ops?: string;
  external_notes?: string;
  internal_notes?: string;
  special_request?: string;
  _destroy?: boolean;
}

/**
 * Settled on-screen timings sent on update (GRAIN-2406). All ISO timestamps.
 * ready_to_eat_at / consume_by are derived server-side and not accepted.
 */
export interface UpdateEventTimingDetails {
  pickup_time?: string;
  setup_time?: string;
  serving_time?: string;
  teardown_time?: string;
}

export interface UpdateEventOverriddenTimings {
  pickup: boolean;
  setup: boolean;
  teardown: boolean;
}

export interface UpdatePriceModifierAttributes {
  // Persisted PriceModifier id; omit for new rows. Set with `_destroy: true` to remove.
  id?: number;
  modifier_type?: 'Discount' | 'Surcharge' | null;
  modifier_id?: number | null;
  name?: string;
  description?: string;
  applied_amount?: string;
  /** The formula value for the current context; differs from applied_amount only when the author overrode it. */
  calculated_amount?: string;
  position?: number;
  auto_applied?: boolean;
  _destroy?: boolean;
}

export interface UpdateEventPayload {
  event_type?: string;
  /** Delivery vs self-pickup. On self-pickup the backend zeroes the fee and pins the Grain Kitchen logistics. */
  fulfilment_type?: FulfilmentType;
  scheduled_at?: string;
  /** Settled pickup/setup/serving/teardown; the backend re-derives on a serving-time change. */
  timing_details?: UpdateEventTimingDetails;
  /**
   * Which of the `timing_details` above the rep typed. The backend honours
   * exactly these and derives the rest, so a flagged time survives a same-save
   * fulfilment-method / format / buffer change. Must always describe the values
   * currently on screen — the form clears a flag whenever it resets that field.
   */
  overridden_timings?: UpdateEventOverriddenTimings;
  /** Combined pre-setup date + time (Buffet only). `null` clears the persisted value. */
  pre_setup_date_time?: string | null;
  pax_count?: number;
  /** Product-line unit price. `null` clears a previously-set value. */
  price_per_pax?: number | null;
  /** Delivery fee. Mirrors Papercut events.delivery_fee (NOT NULL); a cleared field sends 0. */
  delivery_fee?: number;
  metadata?: UpdateEventMetadata;
  logistics_info?: UpdateEventLogisticsInfo;
  host_info?: UpdateEventHostInfo;
  line_items_attributes?: UpdateEventLineItemAttributes[];
  price_modifiers_attributes?: UpdatePriceModifierAttributes[];
  /** Spine order to link; empty string unlinks. Inert while the linking flag is off. */
  spine_order_identifier?: string;
}

export interface UpdateEventParams {
  orderId: number;
  eventId: number;
  payload: UpdateEventPayload;
}

export interface UpdateEventResponse {
  event: ApiEvent;
  publish_blockers: PublishBlocker[];
}

/** The saved event plus the publish blockers still outstanding after the save. */
export interface UpdateEventResult {
  event: ApiEvent;
  publishBlockers: PublishBlocker[];
}

// ─── Update Event API ───────────────────────────────────────────────────────

export async function updateEvent({ orderId, eventId, payload }: UpdateEventParams): Promise<UpdateEventResult> {
  const response = await apiClient.patch<UpdateEventResponse>(
    `/api/orders/${orderId}/events/${eventId}`,
    { event: payload },
  );
  // Defaulted rather than asserted: create/other callers of this endpoint
  // shape predate the sibling key, and an absent key means "no blockers".
  return { event: response.data.event, publishBlockers: response.data.publish_blockers ?? [] };
}

// ─── Delete Event API ───────────────────────────────────────────────────────

export interface DeleteEventParams {
  orderId: number;
  eventId: number;
}

// Soft delete — the server discards the event rather than destroying it.
export async function deleteEvent({ orderId, eventId }: DeleteEventParams): Promise<void> {
  await apiClient.delete(`/api/orders/${orderId}/events/${eventId}`);
}

// ─── Calculate Event Timings ────────────────────────────────────────────────

export interface CalculateEventTimingsParams {
  event_type: string;
  serving_time: string;
  pickup_time?: string;
  setup_time?: string;
  teardown_time?: string;
  delivery_buffer?: number;
  mode?: 'delivery' | 'pickup';
}

export interface EventTimingsPayload {
  pickup_time: string;
  setup_time: string;
  serving_time: string;
  teardown_time: string;
  ready_to_eat_at: string;
  consume_by: string;
}

export interface EventTimingsResponse {
  event_timings: EventTimingsPayload;
  warnings: Record<string, string>;
}

export async function calculateEventTimings(
  params: CalculateEventTimingsParams,
): Promise<EventTimingsResponse> {
  const response = await apiClient.post<EventTimingsResponse>(
    '/api/events/timing_calculations',
    params,
  );
  return response.data;
}

// ─── Generate Event Orders ──────────────────────────────────────────────────

export interface GenerateEventOrdersPayload {
  /** Optional: when omitted, the backend derives the date(s) from event_ids. */
  date?: string;
  document_types: string[];
  all_events_on_date?: boolean;
  event_ids?: number[];
}

export interface GenerateEventOrdersResponse {
  async_task: { id: number };
}

export async function generateEventOrders(
  payload: GenerateEventOrdersPayload,
): Promise<GenerateEventOrdersResponse> {
  const response = await apiClient.post<GenerateEventOrdersResponse>(
    '/api/events/generate_event_orders',
    payload,
  );
  return response.data;
}

// ─── Mapper ─────────────────────────────────────────────────────────────────

export function mapFormStateToPayload(event: EventFormState): CreateEventPayload {
  const metadata: CreateEventMetadata = {};

  if (event.form) metadata.form = event.form;
  if (event.productLine) metadata.product_line = event.productLine;
  // Create omits empty tags (a new event has nothing to clear); the update
  // mapper instead sends [] explicitly so removing the last tag clears it.
  if (event.tags.length > 0) metadata.tags = event.tags;
  if (event.servingTime) metadata.serving_time = event.servingTime;
  if (event.description) metadata.description = event.description;
  if (event.specialInstructions) metadata.special_instructions = event.specialInstructions;

  // Lines/Ways persist to logistics_info, not metadata. Zeros mean "untouched",
  // so omit them (and the whole key when both are 0) to avoid sending values the
  // rep never set.
  const logisticsInfo: CreateEventLogisticsInfo = {};
  if (event.lines) logisticsInfo.lines = event.lines;
  if (event.ways) logisticsInfo.ways = event.ways;

  return {
    // scheduled_at is the serving datetime the backend derives timing from, so
    // fold the picked serving time into the picked date. Falls back to the raw
    // date when no serving time is set.
    scheduled_at: event.scheduledAt
      ? (combineDateAndTime(event.scheduledAt, event.servingTime) ?? event.scheduledAt.toISOString())
      : '',
    pax_count: event.paxCount,
    metadata,
    ...(Object.keys(logisticsInfo).length > 0 ? { logistics_info: logisticsInfo } : {}),
  };
}
